import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeft, Receipt, Search, RefreshCw, ChevronLeft, ChevronRight, Calendar, DollarSign, SlidersHorizontal } from 'lucide-react';
import { adminApiClient } from '../lib/api';

interface Transaction {
  _id: string;
  donorName?: string;
  amount?: number;
  currency?: string;
  donationDate?: string;
  transactionType?: string;
  num?: string;
  productOrService?: string;
  memoOrDescription?: string;
  qty?: number;
  salesPrice?: number;
  balance?: number;
  status?: string;
}

interface LegacyTransactionsPageProps {
  devotee: {
    _id: string;
    donorId?: number;
    name?: string;
    donorFullName?: string;
    fullName?: string;
    email?: string;
  };
  onBack: () => void;
}

const fmt = (n?: number) =>
  n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const LegacyTransactionsPage: React.FC<LegacyTransactionsPageProps> = ({ devotee, onBack }) => {
  const [items, setItems] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const limit = 50;
  const totalPages = Math.ceil(total / limit);

  const donorId = devotee.donorId;
  const displayName = devotee.name || devotee.donorFullName || devotee.fullName || devotee.email || 'Unknown';

  const fetchTransactions = useCallback(async (currentPage: number) => {
    if (donorId == null) return;
    setLoading(true);
    try {
      const res = await adminApiClient.getLegacyTransactions(donorId, {
        page: currentPage,
        limit,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        transactionType: transactionType || undefined,
      });
      if (res.success) {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        toast.error(res.message || 'Failed to load transactions');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [donorId, startDate, endDate, transactionType]);

  useEffect(() => {
    fetchTransactions(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = () => {
    setPage(1);
    fetchTransactions(1);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setPage(1);
    setTimeout(() => fetchTransactions(1), 0);
  };

  const totalAmount = items.reduce((sum, i) => sum + (i.amount || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={onBack}
          className="mt-1 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[var(--color-primary)]" />
            Transaction History
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Legacy transactions for <span className="font-medium text-gray-700">{displayName}</span>
            {devotee.email && <span className="text-gray-400"> · {devotee.email}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters ? 'border-[var(--color-primary)] text-[var(--color-primary)] bg-[color-mix(in_srgb,var(--color-primary)_8%,white)]' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <button
            onClick={() => fetchTransactions(page)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Records</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Page Total Amount</p>
          <p className="text-2xl font-bold text-[var(--color-primary)] mt-1">{fmt(totalAmount)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Donor ID</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">#{donorId ?? '—'}</p>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                <Calendar className="inline w-3.5 h-3.5 mr-1" />Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                <Calendar className="inline w-3.5 h-3.5 mr-1" />End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Transaction Type
              </label>
              <input
                type="text"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                placeholder="e.g. Credit Memo, Invoice…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg bg-[linear-gradient(90deg,var(--color-primary),var(--color-tertiary))] hover:opacity-90"
            >
              <Search className="w-4 h-4" /> Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-left px-5 py-3 font-semibold">Type</th>
                <th className="text-left px-5 py-3 font-semibold">Ref #</th>
                <th className="text-left px-5 py-3 font-semibold">Product / Service</th>
                <th className="text-left px-5 py-3 font-semibold">Memo</th>
                <th className="text-right px-5 py-3 font-semibold">Qty</th>
                <th className="text-right px-5 py-3 font-semibold">Unit Price</th>
                <th className="text-right px-5 py-3 font-semibold">Amount</th>
                <th className="text-right px-5 py-3 font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-gray-400">
                    No transactions found for this donor.
                  </td>
                </tr>
              ) : (
                items.map((t) => (
                  <tr key={t._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{fmtDate(t.donationDate)}</td>
                    <td className="px-5 py-3">
                      {t.transactionType ? (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                          {t.transactionType}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-600 tabular-nums">{t.num || '—'}</td>
                    <td className="px-5 py-3 text-gray-600 max-w-[200px] truncate" title={t.productOrService}>{t.productOrService || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 max-w-[220px] truncate" title={t.memoOrDescription}>{t.memoOrDescription || '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-gray-700">{t.qty ?? '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-gray-700">{fmt(t.salesPrice)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold text-gray-900">
                      <span className="flex items-center justify-end gap-1">
                        <DollarSign className="w-3.5 h-3.5 opacity-50" />
                        {t.amount != null ? Number(t.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-gray-600">{fmt(t.balance)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={7} className="px-5 py-3 text-right text-sm font-semibold text-gray-600">
                    Page Total:
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-bold text-[var(--color-primary)]">
                    {fmt(totalAmount)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} — {total} records total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-200 hover:bg-white disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyTransactionsPage;
